import React, {ReactNode, useContext, useEffect, useRef, useState} from 'react';
import {Alert, Button, Card, Modal, Spin, Table, Tooltip} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {DeleteOutlined, EditOutlined, FileTextOutlined, PlusOutlined} from "@ant-design/icons";
import ToothComp from "./Tooth";
import type {ColumnsType} from 'antd/es/table';
import PatientTreatmentStatusModal from "./PatientTreatmentStatusModal";
import QuickStatusPalette from "./QuickStatusPalette";
import {buildStatusToothCreate, conditionForLetter, getToothRenderStatus, isTypingTarget, latestToothStatusForTooth, normalizeShortcutKey} from "../../utils/conditionShortcuts";
import {
    Appointment,
    Patient,
    StatusResponse,
    Tooth, ToothConditionCode,
    ToothStatus,
    ToothWithRelationships
} from "../../api-client";
import {AlertContext} from "../../context/AlertContext";
import {getPatientApi} from "../../api/apiInstances";
import {ALERT_DURATIONS} from "../../constants/alerts";
// @ts-ignore
import {v4 as uuidv4} from 'uuid';
import {isMobile, isMobileOnly} from "react-device-detect";
import {DentobookApplicationContext} from "../../context/DentobookApplicationContext";
import logger from "../../utils/logger";
import {resolveHistoricalLabel} from "../../utils/historyUtils";

interface DataType {
    key: string;
    date: string;
    conditionId: string;
    condition: string;
    tooth: string;
    notes?: string;
    actions: ReactNode;
    toothPositionId?: string;
}

interface PatientToothStatusCardProps {
    multiSelectionMode: boolean;
    patient: Patient;
    appointment: Appointment;
    teethPositions: ToothWithRelationships[];
    statuses: StatusResponse[];
    nzisToothConditionCodes: ToothConditionCode[];
    fetchTeeth: Function;
}

const PatientToothStatusCard = ({
                                    multiSelectionMode,
                                    appointment,
                                    nzisToothConditionCodes,
                                    teethPositions,
                                    fetchTeeth,
                                    statuses
                                }: PatientToothStatusCardProps) => {

    const MILK_TEETH = 'milkTeeth';
    const PERMANENT_TEETH = 'permanentTeeth';

    const intl = useIntl();

    const {locale} = useContext(DentobookApplicationContext);
    const {setVisible, setError} = useContext(AlertContext);

    const [mode, setMode] = useState<'create' | 'edit'>('create');

    const [loading, setLoading] = useState<boolean>(false);

    const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState<boolean>(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [selectedTeeth, setSelectedTeeth] = useState<Tooth[]>([]);
    const [selectedToothStatuses, setSelectedToothStatuses] = useState<StatusResponse[]>([]);

    const [selectedTableStatus, setSelectedTableStatus] = useState<ToothStatus | undefined>(undefined);

    const [teethStatuses, setTeethStatuses] = useState<ToothStatus[]>([]);

    // SINGLE SOURCE OF TRUTH for the quick palette: the tooth whose floating
    // palette is active. While set, that tooth's normal tooltip is fully
    // suppressed (no overlap / blink), and the tooth+palette behave as one hover
    // zone. `quickSaving` guards apply/clear + keyboard while a request is in flight.
    const [activeQuickToothId, setActiveQuickToothId] = useState<string | null>(null);
    const [quickSaving, setQuickSaving] = useState<boolean>(false);

    // Close debounce only (no open delay — opening synchronously on pointer enter
    // is what prevents the tooth tooltip from flashing before the palette appears).
    const closeTimer = useRef<ReturnType<typeof setTimeout>>();

    // Refs mirror state for the single scoped keydown listener (registered once),
    // avoiding re-binding the listener on every hover change.
    const activeToothRef = useRef<ToothWithRelationships | null>(null);
    const statusesRef = useRef<StatusResponse[]>(statuses);
    const modalOpenRef = useRef<boolean>(false);
    const multiSelectionRef = useRef<boolean>(multiSelectionMode);
    const quickSavingRef = useRef<boolean>(false);
    // Always points at the latest quick-save handler so the once-registered
    // keydown listener never calls a stale closure (e.g. after the selected
    // appointment changes).
    const quickSaveHandlerRef = useRef<(tooth: ToothWithRelationships, conditionDefinition: StatusResponse) => void>();

    const patientApi = getPatientApi();

    useEffect(() => {
        statusesRef.current = statuses;
    }, [statuses]);

    const closeQuickPalette = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        activeToothRef.current = null;
        setActiveQuickToothId(null);
    };

    useEffect(() => {
        multiSelectionRef.current = multiSelectionMode;
        // Hover-driven palette is meaningless in multi-selection mode.
        if (multiSelectionMode) {
            closeQuickPalette();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiSelectionMode]);

    useEffect(() => {
        modalOpenRef.current = isTreatmentModalOpen || isConfirmModalOpen;
    }, [isTreatmentModalOpen, isConfirmModalOpen]);

    useEffect(() => {
        quickSavingRef.current = quickSaving;
    }, [quickSaving]);

    useEffect(() => {
        if (!appointment) {
            return;
        }

        loadData();
    }, []);

    useEffect(() => {
        if (!appointment) {
            return;
        }

        loadData();
    }, [appointment]);

    useEffect(() => {
        // Guard: do not re-open (or reset to create mode) if the modal is already
        // open in edit mode. Without this guard, releasing Command/Meta while an
        // edit-mode status modal is open triggers openTreatmentModal(selectedTeeth,
        // undefined), overwriting mode→'create' and clearing selectedTableStatus.
        if (!multiSelectionMode && selectedTeeth.length && !isTreatmentModalOpen) {
            openTreatmentModal(selectedTeeth, undefined);
        }
    }, [multiSelectionMode]);

    useEffect(() => {
        if (!isTreatmentModalOpen) {
            setSelectedTeeth([]);
        }
    }, [isTreatmentModalOpen]);

    // `silent` skips the full-card Spin so the chart stays visible during rapid
    // quick-status entry (the tooth re-render is feedback enough). Returns a
    // promise so callers can await a fresh, reconciled state.
    const loadData = (silent: boolean = false): Promise<void> => {
        if (!appointment.id) {
            return Promise.resolve();
        }

        if (!silent) {
            setLoading(true);
        }

        return patientApi.fetchPatientToothStatuses(appointment.id!)
            .then(res => {
                // teethStatuses (this endpoint filters soft-deletes) is the SINGLE
                // source of truth for the odontogram's per-tooth status.
                setTeethStatuses(res.data);
                return fetchTeeth();
            })
            .then(() => {
                if (!silent) {
                    setLoading(false);
                }
            })
            .catch(err => {
                logger.log(err);
                if (!silent) {
                    setLoading(false);
                }
            });
    };

    // The patient/appointment-specific ToothStatus record for a tooth, taken from
    // the SAME source the table edit/delete use (teethStatuses). Its `.id` is the
    // real ToothStatus id required by edit + delete — never a condition id. This
    // is the one lookup used for the badge, active shortcut, clear, edit & delete.
    const getExistingToothStatusForTooth = (tooth?: ToothWithRelationships | null): ToothStatus | undefined =>
        latestToothStatusForTooth(teethStatuses, tooth?.id);

    // Immediately persist a condition onto a tooth, refresh quietly, and KEEP the
    // palette open on the same tooth so the dentist gets instant confirmation and
    // can move on. On failure the palette stays open and a concise error shows.
    const handleQuickSave = (tooth: ToothWithRelationships, conditionDefinition: StatusResponse) => {
        if (!appointment?.id || quickSavingRef.current) {
            return;
        }

        setQuickSaving(true);
        patientApi.createToothStatus(appointment.id, [buildStatusToothCreate(tooth.id, conditionDefinition)])
            // Await the reconciling reload so the badge/colour reflect fresh state.
            .then(() => loadData(true)) // palette stays open; re-render highlights the applied letter
            .catch(err => {
                logger.log(err);
                setError(intl.formatMessage({id: 'quick_status_save_failed'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            })
            .finally(() => setQuickSaving(false));
    };

    // Quick-clear: remove the tooth's current status via the SAME delete API and
    // SAME id (ToothStatus.id from teethStatuses) the table trash icon uses.
    const handleQuickClear = (existingToothStatus: ToothStatus | undefined) => {
        if (!appointment?.id || quickSavingRef.current || !existingToothStatus?.id) {
            // No real ToothStatus id => never fire a DELETE with a wrong id.
            logger.log('quick-clear skipped: no deletable ToothStatus id for tooth');
            return;
        }

        const deletedId = existingToothStatus.id;
        setQuickSaving(true);
        patientApi.deleteToothStatus(appointment.id, deletedId)
            .then(() => {
                // Optimistically drop the record from the single source of truth so
                // the badge / colour / clear icon vanish immediately, then reconcile.
                setTeethStatuses(prev => prev.filter(ts => ts.id !== deletedId));
                return loadData(true);
            })
            .catch(err => {
                logger.log(err);
                setError(intl.formatMessage({id: 'quick_status_save_failed'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            })
            .finally(() => setQuickSaving(false));
    };

    // Keep the keydown listener's handler reference current (assigned every render).
    quickSaveHandlerRef.current = handleQuickSave;

    // Open the full modal for a tooth. If the tooth already has a status, open it
    // in EDIT mode on the existing ToothStatus (same path as the table pencil) so
    // the condition is pre-selected — never a blank create modal that would
    // duplicate the status.
    const openToothModal = (tooth: ToothWithRelationships) => {
        const existingToothStatus = getExistingToothStatusForTooth(tooth);
        setSelectedTeeth([]);
        closeQuickPalette();
        openTreatmentModal([tooth], existingToothStatus);
    };

    // Tooth + palette are treated as ONE hover zone (the palette is a DOM
    // descendant of the cell, so moving cursor tooth->palette never fires
    // pointerleave). Opening synchronously on enter (no delay) is what stops the
    // tooth tooltip from flashing before the palette appears.
    const handleCellPointerEnter = (tooth: ToothWithRelationships, event: React.PointerEvent) => {
        if (multiSelectionMode || event.pointerType === 'touch') {
            return; // touch is handled via click; hover-open is mouse/pen only
        }
        if (closeTimer.current) clearTimeout(closeTimer.current);
        activeToothRef.current = tooth;
        setActiveQuickToothId(tooth.id ?? null);
    };

    const handleCellPointerLeave = (event: React.PointerEvent) => {
        if (event.pointerType === 'touch') {
            return;
        }
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(closeQuickPalette, 150);
    };

    const handleToothClick = (tooth: ToothWithRelationships) => {
        if (multiSelectionMode) {
            if (selectedTeeth.indexOf(tooth) === -1) {
                setSelectedTeeth(selectedTeeth.concat(tooth));
            } else {
                setSelectedTeeth(selectedTeeth.filter(item => item.id !== tooth.id));
            }
            return;
        }

        // Touch devices have no reliable hover: first tap reveals the palette
        // (with a "more" button to reach the full modal). Desktop click opens the
        // full modal directly (now pre-loading any existing status).
        if (isMobile && activeQuickToothId !== tooth.id) {
            activeToothRef.current = tooth;
            setActiveQuickToothId(tooth.id ?? null);
            return;
        }

        openToothModal(tooth);
    };

    // Single scoped keydown listener for the whole odontogram card. It only acts
    // when a tooth is the active quick tooth, no modal is open, not in
    // multi-selection, and focus is not in a text-entry surface.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const tooth = activeToothRef.current;
            if (!tooth || modalOpenRef.current || multiSelectionRef.current) {
                return;
            }
            // Escape closes the palette (even mid-save guard below doesn't block it).
            if (event.key === 'Escape') {
                event.preventDefault();
                closeQuickPalette();
                return;
            }
            if (quickSavingRef.current || isTypingTarget(document.activeElement)) {
                return;
            }
            const letter = normalizeShortcutKey(event.key);
            if (!letter) {
                return;
            }
            const conditionDefinition = conditionForLetter(letter, statusesRef.current);
            if (!conditionDefinition) {
                return;
            }
            event.preventDefault();
            quickSaveHandlerRef.current?.(tooth, conditionDefinition);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => {
            window.removeEventListener('keydown', onKeyDown);
            if (closeTimer.current) clearTimeout(closeTimer.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Outside-click / outside-tap closes the palette (mainly for touch; mouse is
    // covered by pointerleave). A pointerdown inside any tooth cell is ignored so
    // switching teeth or pressing palette buttons does not prematurely close it.
    useEffect(() => {
        if (!activeQuickToothId) {
            return;
        }
        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null;
            if (target && target.closest('.tooth-cell')) {
                return;
            }
            closeQuickPalette();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeQuickToothId]);

    const columns: ColumnsType<DataType> = [
        {
            title: useIntl().formatMessage({id: 'date'}),
            dataIndex: 'date',
            key: 'date',
            sorter: (dataOne, dataTwo) => {
                const tokensOne = dataOne.date.split('.');
                const tokensTwo = dataTwo.date.split('.');
                const firstDate = tokensOne[2] + '-' + tokensOne[1] + '-' + tokensOne[0];
                const secondDate = tokensTwo[2] + '-' + tokensTwo[1] + '-' + tokensTwo[0];

                return firstDate.localeCompare(secondDate);
            },
            width: '15%'
        },
        {
            title: useIntl().formatMessage({id: 'condition'}),
            dataIndex: 'condition',
            key: 'condition',
            width: '35%',
            render: (condition: string, record: DataType) => {
                // Strip HTML tags from notes for tooltip display using DOM API
                const stripHtml = (html: string): string => {
                    const tmp = document.createElement('div');
                    tmp.innerHTML = html;
                    return tmp.textContent || tmp.innerText || '';
                };

                const plainTextNotes = record.notes ? stripHtml(record.notes).trim() : '';

                return (
                    <span>
                        {condition}
                        {plainTextNotes && (
                            <Tooltip title={plainTextNotes} placement="topLeft" overlayStyle={{ maxWidth: '300px' }}>
                                <FileTextOutlined style={{ marginLeft: 8, color: '#1890ff', cursor: 'pointer' }} />
                            </Tooltip>
                        )}
                    </span>
                );
            }
        },
        {
            title: useIntl().formatMessage({id: 'tooth'}),
            dataIndex: 'tooth',
            key: 'tooth',
            sorter: (dataOne, dataTwo) => dataOne.tooth.localeCompare(dataTwo.tooth),
            width: '39%'
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: '11%'
        },
    ];

    const data: DataType[] = teethStatuses
        .sort((toothStatusOne, toothStatusTwo) => {
            if (!toothStatusOne.tooth?.id || !toothStatusTwo.tooth?.id) {
                return 0;
            }

            return toothStatusOne.tooth?.id.localeCompare(toothStatusTwo.tooth?.id);
        }).map(toothStatus => {
            if (!teethPositions.length) {
                return undefined;
            }

            const toothPosition =
                teethPositions.filter(toothPosition => toothPosition.id === toothStatus.tooth?.id)[0];

            const status = statuses.filter(statusObject => statusObject.id === toothStatus.statusId);
            const statusDisplayName = resolveHistoricalLabel({ snapshot: toothStatus.statusNameSnapshot, entity: status[0] });

            const toothPositionNumber = toothPosition ? toothPosition.position?.toString() : '';

            const tokens = toothStatus.createdAt?.split('T')[0].split('-');

            let toothPositionDate;
            if (!tokens) {
                toothPositionDate = '';
            } else {
                toothPositionDate = tokens[2] + '.' + tokens[1] + '.' + tokens[0];
            }

            return {
                key: uuidv4(),
                date: toothPositionDate,
                conditionId: status.length > 0 ? status[0].id : toothStatus.statusId,
                condition: statusDisplayName,
                tooth: toothPositionNumber,
                notes: toothStatus.notes || undefined,
                actions: <div style={{display: 'flex', flexDirection: 'row'}}>
                    <Button className="btn-no-border" ghost={false} size="large"
                            style={{padding: 10}}
                            onClick={(event) => {
                                event.stopPropagation();
                                openTreatmentModal(!toothPosition ? [] : [toothPosition], toothStatus);
                            }}><EditOutlined/>
                    </Button>
                    {' '}
                    <Button className="btn-no-border"
                            style={{padding: 10}}
                            ghost={false} size="large"
                            onClick={(event) => {
                                event.stopPropagation();
                                openConfirmModal(toothStatus);
                            }}><DeleteOutlined/></Button>
                </div>,
                toothPositionId: toothStatus.tooth?.id,
            } as DataType;
        })
        .filter(data => typeof data !== 'undefined') as DataType[];

    const tabList = [
        {
            key: PERMANENT_TEETH,
            tab: useIntl().formatMessage({id: 'permTeeth'}),
        },
        {
            key: MILK_TEETH,
            tab: useIntl().formatMessage({id: 'milkTeeth'}),
        },
    ];

    const [activeTabKey, setActiveTabKey] = useState<string>(PERMANENT_TEETH);

    const openTreatmentModal = (teethPositions: Tooth[], toothStatus: ToothStatus | undefined) => {
        if (teethPositions.length === 0) {
            if (toothStatus) {
                const toothStatusesArray = teethStatuses
                    .filter(statusObj => !statusObj.tooth && statusObj.statusId === toothStatus.statusId)
                    .map(item => item.statusId);

                const statusesArray = statuses
                    .filter(statusObj => toothStatusesArray.indexOf(statusObj.id) > -1);

                setMode('edit');
                setSelectedTableStatus(toothStatus);
                setSelectedToothStatuses(statusesArray);
            } else {
                setMode('create');
            }
            setSelectedTeeth([]);
        } else if (teethPositions.length === 1 && teethPositions[0] && teethPositions[0].id) {
            if (toothStatus) {
                const toothStatusesArray = teethStatuses
                    .filter(statusObj => statusObj.tooth?.id === toothStatus.tooth?.id
                        && statusObj.statusId === toothStatus.statusId)
                    .map(item => item.statusId);

                const statusesArray = statuses
                    .filter(statusObj => toothStatusesArray.indexOf(statusObj.id) > -1);

                setMode('edit');
                setSelectedTableStatus(toothStatus);
                setSelectedToothStatuses(statusesArray);
            } else {
                setMode('create');
            }
            setSelectedTeeth([teethPositions[0]]);
        } else {
            setMode('create');
            setSelectedTeeth(teethPositions);
        }
        setIsTreatmentModalOpen(true);
    }

    const openConfirmModal = (toothStatus: ToothStatus) => {
        setConfirmModalOpen(true);
        setSelectedTableStatus(toothStatus);
    }

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin tip={intl.formatMessage({ id: 'loading' })} />
            </div>
        );
    }

    const handleConfirmOk = () => {
        setConfirmModalOpen(false);

        if (!appointment || !selectedTableStatus) {
            return;
        }

        patientApi.deleteToothStatus(appointment.id!, selectedTableStatus.id!)
            .then(() => {
                setVisible(true);
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                loadData();
            })
            .catch(err => logger.log(err));
    };

    const handleConfirmCancel = () => {
        setConfirmModalOpen(false);
    };

    return (
        <>
            <Modal
                transitionName=""
                okText={<FormattedMessage id="confirm"/>}
                className="dentobook-modal"
                okButtonProps={{
                    className: "modal-btn"
                }}
                cancelButtonProps={{
                    className: "modal-btn"
                }}
                cancelText={<FormattedMessage id="cancel"/>}
                open={isConfirmModalOpen} onOk={handleConfirmOk} onCancel={handleConfirmCancel}>
                <br/>
                <strong><FormattedMessage id="tooth_status_delete_confirm"/></strong>
            </Modal>
            <PatientTreatmentStatusModal allTeethPositions={teethPositions}
                                         mode={mode}
                                         selectedTableStatus={selectedTableStatus!}
                                         appointment={appointment.id!}
                                         toothObjs={selectedTeeth}
                                         loadData={loadData}
                                         allTeethStatuses={teethStatuses}
                                         selectedStatuses={selectedToothStatuses}
                                         statuses={statuses} isModalOpen={isTreatmentModalOpen}
                                         setIsModalOpen={setIsTreatmentModalOpen}
                                         title={selectedTeeth.map(tooth => tooth?.position?.toString()).join(', ')}/>
            <Alert
                message={intl.formatMessage({id: 'tooth_status_quick_help'})}
                type="info"
                showIcon
                style={{marginTop: 16, marginBottom: 16}}
            />
            <Card tabList={tabList}
                  rootClassName="patient-treatment"
                  defaultActiveTabKey={activeTabKey}
                  onTabChange={key => setActiveTabKey(key)}
                  tabBarExtraContent={<Button shape="round"
                                              className="dentobook-btn-secondary dentobook-btn-medium color-primary"
                                              type={"default"}
                                              onClick={() => openTreatmentModal([], undefined)}><PlusOutlined/>
                      <FormattedMessage
                          id="add"/></Button>}
                  style={{
                      overflow: isMobileOnly ? 'scroll' : 'initial',
                      height: 'auto',
                      marginBottom: isMobile ? 200 : 80
                  }}>
                <div>
                    <div className={activeTabKey === MILK_TEETH ? "milk-teeth-card" : "permanent-teeth-card"}>
                        {teethPositions
                            .filter(toothPos => {
                                if (activeTabKey === PERMANENT_TEETH) return toothPos.position && toothPos.position < 50;
                                else return toothPos.position && toothPos.position > 50;
                            })
                            .map(toothPosition => {
                                // SINGLE SOURCE OF TRUTH for this tooth's status: the patient
                                // ToothStatus records from teethStatuses (the endpoint that
                                // filters soft-deletes) — the SAME source the table edit/delete
                                // use. Badge, colour, clear, edit and delete therefore always
                                // agree, and a cleared status disappears immediately.
                                const toothStatusesForTooth = teethStatuses.filter(
                                    ts => ts.toothId === toothPosition.id || ts.tooth?.id === toothPosition.id);
                                // ONE lookup feeds badge, active shortcut, clear & edit.
                                const {existingToothStatus, conditionDefinition, statusLabel, headerColor} =
                                    getToothRenderStatus(toothPosition, teethStatuses, statuses);

                                // Long name for the (multi-status) tooltip text only.
                                const latestStatusLabel =
                                    conditionDefinition?.displayName ?? conditionDefinition?.name ?? existingToothStatus?.statusNameSnapshot ?? '-';
                                let tooltipMessage;
                                if (toothStatusesForTooth.length > 1) {
                                    // Deduplicate: multiple ToothStatus rows can share the same
                                    // statusId (e.g. same condition applied several times on
                                    // different dates). Only render each unique label once.
                                    const seenLabels = new Set<string>();
                                    tooltipMessage = (
                                        <div>
                                            {toothStatusesForTooth.reduce((acc: JSX.Element[], toothStatus: ToothStatus) => {
                                                const foundConditionDefinition = statuses.find(cd => cd.id === toothStatus.statusId);
                                                const statusLabel =
                                                    foundConditionDefinition?.displayName ?? foundConditionDefinition?.name ?? toothStatus?.statusNameSnapshot ?? '-';
                                                if (!seenLabels.has(statusLabel)) {
                                                    seenLabels.add(statusLabel);
                                                    acc.push(<div key={toothStatus.id}>{`${intl.formatMessage({id: 'condition'})}: ${statusLabel}`}</div>);
                                                }
                                                return acc;
                                            }, [])}
                                        </div>
                                    );
                                } else {
                                    tooltipMessage = existingToothStatus
                                        ? `${intl.formatMessage({id: 'condition'})}: ${latestStatusLabel}`
                                        : intl.formatMessage({id: 'no_diagnose'});
                                }

                                const paletteOpen = activeQuickToothId === toothPosition.id && !multiSelectionMode;
                                // Highlight the currently-applied condition.
                                const activeConditionId = existingToothStatus?.statusId ?? conditionDefinition?.id ?? null;
                                return (
                                    <div
                                        className="tooth-cell"
                                        key={toothPosition.id}
                                        onPointerEnter={(e) => handleCellPointerEnter(toothPosition, e)}
                                        onPointerLeave={handleCellPointerLeave}
                                    >
                                        <button
                                            className="plain-button"
                                            onKeyDown={e => e.preventDefault()}
                                            onClick={() => handleToothClick(toothPosition)}
                                        >
                                            <ToothComp position={toothPosition.position || 0}
                                                       statusLabel={statusLabel}
                                                       selected={multiSelectionMode && selectedTeeth.indexOf(toothPosition) > -1}
                                                       statusColor={headerColor}
                                                       headerColor={headerColor}
                                                       milk={activeTabKey === MILK_TEETH}
                                                       multiSelectionMode={multiSelectionMode}
                                                       tooltipContent={tooltipMessage}
                                                       // Quick-status is the primary interaction here and the
                                                       // status badge already shows the condition, so the old
                                                       // portaled tooth tooltip is disabled completely for the
                                                       // odontogram grid — it can never mount/flash on hover.
                                                       disableTooltip={true}
                                            />
                                        </button>
                                        {paletteOpen && (
                                            <div className="quick-status-popover">
                                                <QuickStatusPalette
                                                    conditionDefinitions={statuses}
                                                    activeConditionId={activeConditionId}
                                                    saving={quickSaving}
                                                    showMore
                                                    showClear={!!existingToothStatus}
                                                    clearDisabled={quickSaving || !existingToothStatus?.id}
                                                    onSelect={(conditionDef) => handleQuickSave(toothPosition, conditionDef)}
                                                    onClear={() => handleQuickClear(existingToothStatus)}
                                                    onMore={() => openToothModal(toothPosition)}
                                                />
                                            </div>
                                        )}
                                        {paletteOpen && (
                                            // Inline (non-portaled) status label anchored BELOW the tooth,
                                            // shown only for the active tooth — replaces the old AntD tooltip
                                            // so there is no 0,0 flash and no overlap with the palette (above).
                                            <div className="tooth-status-hover-label">
                                                {tooltipMessage}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                    <Table
                        locale={{emptyText: intl.formatMessage({id: 'no_data'})}}
                        className={`${activeTabKey === MILK_TEETH ? "milk-teeth-table" : "permanent-teeth-table"} teeth-table pt-2`}
                        pagination={false}
                        columns={columns}
                        dataSource={data}
                        onRow={(record: DataType) => ({
                            onClick: () => {
                                const toothPosition = teethPositions.find(tp => tp.id === record.toothPositionId);
                                const toothStatus = teethStatuses.find(toothStatus => toothStatus.tooth?.id === record.toothPositionId
                                    && toothStatus.statusId === record.conditionId);
                                openTreatmentModal(!toothPosition ? [] : [toothPosition], toothStatus);
                            },
                        })}
                    />
                </div>
            </Card>
        </>
    );
};

export default PatientToothStatusCard;